import type { QualificationErrorResponse } from '@t/serviceQualification.types';

export const extractNestedError = (rawMessage: string) => {
  try {
    const jsonString = rawMessage.substring(rawMessage.indexOf('{'), rawMessage.lastIndexOf('}') + 1);
    const nestedData = JSON.parse(jsonString);
    return {
      code: nestedData.errorCode ?? nestedData.code,
      message: nestedData.errorDescription ?? nestedData.message,
    };
  } catch {
    return null;
  }
};

type ParsedApiError = { code: string; message?: string };

/** Extracts error code + message from an Axios error response body (any format). */
export const extractApiError = (rawData: unknown): ParsedApiError | null => {
  // Raw string body: backend may proxy JSON without application/json Content-Type
  if (typeof rawData === 'string') {
    const nested = extractNestedError(rawData);
    return nested?.code ? { code: nested.code, message: nested.message } : null;
  }

  const data = rawData as (QualificationErrorResponse & { kind?: string }) | undefined;
  if (!data || typeof data !== 'object') return null;

  // Direct fields: { errorCode, errorDescription } or { code, message } or { reason, message }
  const directCode = data.errorCode ?? data.code ?? data.reason;
  const directMessage = data.errorDescription ?? data.message;
  if (directCode) return { code: directCode, message: directMessage };

  // Address-lookup proxy: { kind }. Its own field name because the value is a
  // closed set of failure kinds, not a vendor code the frontend has to interpret.
  if (data.kind) return { code: data.kind, message: directMessage };

  // GLDS envelope: { error: { code, message } }
  if (data.error?.code) return { code: data.error.code, message: data.error.message ?? directMessage };

  // Nested JSON within the message field
  if (typeof directMessage === 'string' && directMessage.includes('{')) {
    const nested = extractNestedError(directMessage);
    return nested?.code ? { code: nested.code, message: nested.message } : null;
  }

  return null;
};
